import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { ArtToothForDiagram, ArtTreatmentIntegrity, ArtTreatmentDefinition } from '../../models';
import { isArtTreatmentUnassigned } from '../../constants/art-treatment.constants';
import { RxIntuitiveIndicationMapperService } from '@modules/rx-intuitive-teeth-orchestrator/services/rx-intuitive-indication-mapper.service';

@Component({
	selector: 'rx-intuitive-art-tooth',
	templateUrl: './rx-intuitive-art-tooth.component.html',
	styleUrls: ['./rx-intuitive-art-tooth.component.scss'],
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule, TranslateModule],
	host: {
		'[class]': 'indicationHostClass',
		'[class.rx-intuitive-art-tooth]': 'true',
		'[class.tooth--first-selected]': 'isFirstSelected',
		'[class.tooth--edit-mode]': 'isEditMode',
		'[class.tooth--assigned]': 'isAssigned',
		'[class.tooth--disabled]': 'isToothDisabled',
		'[class.tooth--selected]': 'tooth.isSelected',
		'[class.tooth--has-treatment]': 'hasAssignedTreatment',
		'[class.tooth--has-integrity]': 'hasAssignedIntegrity',
		'[attr.data-integrity]': 'integrityClassName',
		'[class.tooth--single-treatment]': 'hasSingleTreatment',
		'[class.tooth--multiple-treatments]': 'hasMultipleTreatments',
		'[class.tooth--lower]': "numberPosition === 'above'",
		'[attr.data-primary-treatment]': 'primaryTreatmentClass',
		'[class.tooth--invalid]': 'isInvalidTooth || isInvalidForSend'
	}
})
export class RxIntuitiveArtToothComponent {
	@Input() tooth!: ArtToothForDiagram;
	@Input() displayNumber!: string | number;
	@Input() symbolId!: string;
	@Input() numberPosition: 'above' | 'below' = 'below';
	@Input() chartReadOnly = false;
	@Output() toothClicked = new EventEmitter<number>();

	get isFirstSelected(): boolean {
		return this.tooth.isSelected && this.indicationMapper.isDefaultIndication(this.tooth.indicationId);
	}
	get isEditMode(): boolean {
		return this.tooth.isSelected && !this.indicationMapper.isDefaultIndication(this.tooth.indicationId);
	}
	get isAssigned(): boolean {
		return !this.tooth.isSelected && !this.indicationMapper.isDefaultIndication(this.tooth.indicationId);
	}
	get isToothDisabled(): boolean {
		return !!this.tooth?.isDisabled;
	}
	get indicationHostClass(): string {
		return this.treatmentClassNames.join(' ') || 'default';
	}
	get treatmentClassNames(): string[] {
		const { integrity, definitions } = this.tooth.artTreatment;
		const integrityName = ArtTreatmentIntegrity[integrity];
		const definitionNames = definitions.map(definition => ArtTreatmentDefinition[definition]);

		if (integrity === ArtTreatmentIntegrity.Adjacent) {
			return definitionNames;
		}

		return [integrityName, ...definitionNames];
	}
	get hasMultipleTreatments(): boolean {
		return this.treatmentClassNames.length > 1;
	}
	get hasSingleTreatment(): boolean {
		return this.treatmentClassNames.length === 1;
	}
	get primaryTreatmentClass(): string {
		return this.treatmentClassNames[0] ?? 'default';
	}
	get integrityClassName(): string {
		return ArtTreatmentIntegrity[this.tooth.artTreatment.integrity];
	}
	get hasAssignedTreatment(): boolean {
		const { toothId, artTreatment } = this.tooth;

		return !isArtTreatmentUnassigned(toothId, artTreatment.integrity, artTreatment.definitions);
	}
	get hasAssignedIntegrity(): boolean {
		return this.tooth.artTreatment.integrity !== ArtTreatmentIntegrity.Adjacent;
	}
	get isInvalidTooth(): boolean {
		return this.tooth.isInvalid === true;
	}
	get isInvalidForSend(): boolean {
		return this.tooth.isInvalidForSend === true;
	}

	constructor(private readonly indicationMapper: RxIntuitiveIndicationMapperService) {}

	onToothClick(): void {
		if (this.chartReadOnly || this.isToothDisabled || this.tooth?.toothId == null) {
			return;
		}
		this.toothClicked.emit(this.tooth.toothId);
	}
}
